import React, { useState, useEffect, useCallback } from 'react';
import { getPracticalList, getPracticalPresignedUrl, createPractical, updatePracticalScore, deletePractical, getHousekeeperById } from '../api/housekeeperApi';
import { uploadVideoToCos } from '../api/cosApi';
import './PracticalPage.css';

const VIDEO_TYPE_CONFIG = {
  '育婴师': [
    { type: 'touch_exercise', name: '抚触操', icon: '🤲' },
    { type: 'gas_relief', name: '排气操', icon: '💨' },
    { type: 'passive_exercise', name: '被动操', icon: '🧘' },
    { type: 'active_passive', name: '主被动操', icon: '🏃' },
    { type: 'airplane_hold', name: '飞机抱', icon: '✈️' },
    { type: 'finger_exercise', name: '手指操（八月龄宝宝）', icon: '👆' }
  ],
  '月嫂': [
    { type: 'mom_care', name: '妈妈清洁护理', icon: '🧴' },
    { type: 'postnatal_exercise', name: '产后保健操', icon: '🤸' },
    { type: 'baby_holding', name: '宝宝抱姿', icon: '👶' },
    { type: 'baby_bath', name: '宝宝洗澡', icon: '🛁' },
    { type: 'diaper_change', name: '更换纸尿布', icon: '🩲' }
  ]
};

export default function PracticalPage({ id, navigateTo }) {
  const [housekeeper, setHousekeeper] = useState(null);
  const [videoItems, setVideoItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ratingType, setRatingType] = useState(null);
  const [previewScore, setPreviewScore] = useState(1);

  const role = housekeeper?.role || '育婴师';

  const loadData = useCallback(async () => {
    try {
      const hkRes = await getHousekeeperById(id);
      setHousekeeper(hkRes.data);
      const role = hkRes.data.role || '育婴师';
      const config = VIDEO_TYPE_CONFIG[role] || VIDEO_TYPE_CONFIG['育婴师'];
      const baseItems = config.map(item => ({
        ...item,
        video_url: null,
        score: 0,
        practicalId: null
      }));

      const practicalRes = await getPracticalList(id);
      const records = practicalRes.data || [];
      records.forEach(record => {
        const idx = baseItems.findIndex(i => i.type === record.video_type);
        if (idx >= 0) {
          baseItems[idx].video_url = record.video_url;
          baseItems[idx].score = record.score || 0;
          baseItems[idx].practicalId = record.id;
        }
      });
      setVideoItems(baseItems);
    } catch (err) {
      console.error('加载数据失败:', err);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChooseVideo = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploading(true);
      setUploadProgress(0);

      try {
        const presignedRes = await getPracticalPresignedUrl({
          housekeeper_id: id,
          video_type: type,
          filename: file.name,
          content_type: file.type || 'video/mp4'
        });
        const presignedData = presignedRes.data;

        await uploadVideoToCos(file, presignedData);

        const createRes = await createPractical({
          housekeeper_id: id,
          video_type: type,
          video_url: presignedData.access_url
        });

        const newRecord = createRes.data;
        setVideoItems(prev => prev.map(item =>
          item.type === type
            ? { ...item, video_url: presignedData.access_url, practicalId: newRecord.id }
            : item
        ));

        setUploading(false);
        setRatingType(type);
        setPreviewScore(1);
      } catch (err) {
        setUploading(false);
        console.error('上传失败:', err);
        alert('视频上传失败');
      }
    };
    input.click();
  };

  const handleSetStar = (star) => {
    setPreviewScore(star);
  };

  const handleAdjustScore = (delta) => {
    setPreviewScore(prev => {
      const newScore = Math.round((prev + delta) * 10) / 10;
      return Math.max(1, Math.min(5, newScore));
    });
  };

  const handleConfirmScore = async () => {
    if (!ratingType) return;
    if (previewScore < 1 || previewScore > 5) return;

    const item = videoItems.find(i => i.type === ratingType);
    if (!item?.practicalId) return;

    try {
      await updatePracticalScore(item.practicalId, previewScore);
      setVideoItems(prev => prev.map(i =>
        i.type === ratingType ? { ...i, score: previewScore } : i
      ));
      setRatingType(null);
    } catch (err) {
      console.error('评分失败:', err);
      alert('评分失败');
    }
  };

  const handleEditScore = (type) => {
    const item = videoItems.find(i => i.type === type);
    setRatingType(type);
    setPreviewScore(item.score);
  };

  const handleCancelRating = () => {
    setRatingType(null);
  };

  const handleDelete = async (type) => {
    const item = videoItems.find(i => i.type === type);
    if (!item?.practicalId) return;
    if (!window.confirm('确认删除该实操视频？')) return;

    try {
      await deletePractical(item.practicalId);
      setVideoItems(prev => prev.map(i =>
        i.type === type ? { ...i, video_url: null, score: 0, practicalId: null } : i
      ));
    } catch (err) {
      console.error('删除失败:', err);
      alert('删除失败');
    }
  };

  return (
    <div className="practical-container">
      <div className="practical-header">
        <div className="page-title-section">
          <button className="btn-back" onClick={() => navigateTo('detail', { id })}>← 返回</button>
          <div>
            <h1 className="page-title">实操视频上传</h1>
            <p className="page-subtitle">{role}</p>
          </div>
        </div>
      </div>

      {housekeeper && (
        <div className="hk-brief">
          {housekeeper.photo_url ? (
            <img className="hk-photo" src={housekeeper.photo_url} alt={housekeeper.name} />
          ) : (
            <div className="hk-photo-placeholder">👤</div>
          )}
          <div className="hk-info">
            <span className="hk-name">{housekeeper.name}</span>
            <span className="hk-detail">{housekeeper.age}岁 · {role}</span>
          </div>
        </div>
      )}

      <div className="video-grid">
        {videoItems.map(item => (
          <div className="video-item" key={item.type}>
            <div className="video-icon-wrap" onClick={() => ratingType !== item.type && handleChooseVideo(item.type)}>
              <span className="video-icon">{item.icon}</span>
              {item.video_url && <span className="uploaded-badge">✓</span>}
            </div>
            <span className="video-name">{item.name}</span>

            {ratingType === item.type && (
              <div className="rating-section">
                <div className="rating-label-row">
                  <span className="rating-label">实操评分</span>
                  <div className="star-row">
                    {[1, 2, 3, 4, 5].map(star => (
                      <span
                        key={star}
                        className={`star ${star <= previewScore ? 'star-active' : 'star-gray'}`}
                        onClick={() => handleSetStar(star)}
                      >★</span>
                    ))}
                  </div>
                </div>
                <div className="score-adjust-row">
                  <button className="adjust-btn minus" onClick={() => handleAdjustScore(-0.5)}>-</button>
                  <span className="score-preview">{previewScore}分</span>
                  <button className="adjust-btn plus" onClick={() => handleAdjustScore(0.5)}>+</button>
                </div>
                <div className="rating-actions">
                  <button className="rating-btn confirm" onClick={handleConfirmScore}>确认评分</button>
                  <button className="rating-btn cancel" onClick={handleCancelRating}>取消</button>
                </div>
              </div>
            )}

            {item.score > 0 && ratingType !== item.type && (
              <div className="score-display">
                <span className="score-value">{item.score}</span>
                <span className="score-unit">分</span>
                <span className="score-edit" onClick={() => handleEditScore(item.type)}>修改</span>
              </div>
            )}
            {item.video_url && item.score === 0 && ratingType !== item.type && (
              <span className="no-score">待评分</span>
            )}
            {!item.video_url && (
              <span className="no-video">未上传</span>
            )}
            {item.video_url && ratingType !== item.type && (
              <button className="delete-btn" onClick={() => handleDelete(item.type)}>删除视频</button>
            )}
          </div>
        ))}
      </div>

      {uploading && (
        <div className="uploading-overlay">
          <div className="uploading-content">
            <div className="spinner"></div>
            <div className="uploading-text">正在上传视频...</div>
            {uploadProgress > 0 && <div className="uploading-progress">{uploadProgress}%</div>}
          </div>
        </div>
      )}
    </div>
  );
}